/**
 * Custom hook for authenticated API calls
 *
 * Provides a unified way to make authenticated API requests with token and orgId.
 * Eliminates code duplication across components.
 *
 * @example
 * ```tsx
 * const { getAuthData, authenticatedFetch: fetchWithAuth } = useAuthFetch();
 *
 * const data = await fetchWithAuth('/api/v1/projects');
 * ```
 */

import { useRole } from '@/contexts/RoleContext';
import { useOrgAuthFetch } from '@/hooks/useOrgAuthFetch';

export type { AuthData } from '@/hooks/useOrgAuthFetch';

/**
 * Hook for authenticated API calls
 *
 * Returns:
 * - getAuthData: Get token and orgId (throws if missing)
 * - authenticatedFetch: Wrapper around authenticatedFetch with automatic token/orgId
 */
export function useAuthFetch() {
  const { selectedOrgId } = useRole();
  return useOrgAuthFetch(selectedOrgId);
}
